import React, { useEffect, useMemo, useState } from 'react'
import Note from '../../common/note/Note'
import { NoteType } from '../../../enums/NoteType'
import Button from '../../common/form/inputs/Button'
import kitToolApi, {
    type ExecuteJobSearchRes,
    type GiveJobsFeedbackRes,
    type GiveJobsFeedbackStatus,
    type TargetSalaryCurrency,
    type TargetSalaryMentionedRes,
    type TargetJobLocationMention,
    type TargetJobLocationsMentionedRes,
    type TargetJobTitlesMentionedRes,
    type UsedToWorkAtCompaniesMentionedRes,
} from '@/api/kit/KitToolApi'
import type { KitConvoJobOfferedDto } from '@/api/kit/KitApi'

type Props = {
    activeConvoId: string | null
    isActiveCall: boolean
    apiKey: string
    onApiKeyChange: (nextValue: string) => void
    jobsOffered: KitConvoJobOfferedDto[]
}

const parseCompanyNames = (rawValue: string): string[] => {
    const cleaned = rawValue
        .split(/[\n,]/)
        .map((entry) => entry.trim())
        .filter(Boolean)

    return Array.from(new Set(cleaned))
}

const parseTargetJobTitles = (rawValue: string): string[] => {
    const cleaned = rawValue
        .split(/[\n,]/)
        .map((entry) => entry.trim())
        .filter(Boolean)

    const dedupedByLowerCase = new Map<string, string>()

    for (const entry of cleaned) {
        const dedupeKey = entry.toLowerCase()
        if (dedupedByLowerCase.has(dedupeKey)) {
            continue
        }
        dedupedByLowerCase.set(dedupeKey, entry)
    }

    return Array.from(dedupedByLowerCase.values())
}

const parseTargetLocations = (rawValue: string): TargetJobLocationMention[] => {
    const entries = rawValue
        .split('\n')
        .map((entry) => entry.trim())
        .filter(Boolean)

    const parsed: TargetJobLocationMention[] = []
    const seen = new Set<string>()

    for (const entry of entries) {
        const separatorIndex = entry.indexOf(',')
        if (separatorIndex < 0) {
            continue
        }

        const city = entry.slice(0, separatorIndex).trim()
        const country = entry.slice(separatorIndex + 1).trim()

        if (!city && !country) {
            continue
        }

        const normalizedCity = city || undefined
        const normalizedCountry = country || undefined
        const dedupeKey = `${(normalizedCity ?? '').toLowerCase()}|${(normalizedCountry ?? '').toLowerCase()}`
        if (seen.has(dedupeKey)) {
            continue
        }

        seen.add(dedupeKey)
        parsed.push({
            city: normalizedCity,
            country: normalizedCountry,
        })
    }

    return parsed
}

const formatLocationMention = (
    city: string | null | undefined,
    country: string | null | undefined,
): string => {
    const cityText = city?.trim() ?? ''
    const countryText = country?.trim() ?? ''

    if (cityText && countryText) {
        return `${cityText}, ${countryText}`
    }
    if (cityText) {
        return cityText
    }
    if (countryText) {
        return countryText
    }

    return 'Location unavailable'
}

const TARGET_SALARY_CURRENCIES: TargetSalaryCurrency[] = ['GBP', 'USD', 'EUR']

const parseOptionalNonNegativeNumber = (
    rawValue: string,
): number | undefined | 'invalid' => {
    const trimmed = rawValue.trim()
    if (!trimmed) {
        return undefined
    }

    const parsedValue = Number(trimmed)
    if (!Number.isFinite(parsedValue) || parsedValue < 0) {
        return 'invalid'
    }

    return parsedValue
}

const formatSalaryLabel = (
    currency: TargetSalaryCurrency,
    minSalary: number | null | undefined,
    maxSalary: number | null | undefined,
): string => {
    const minValue =
        typeof minSalary === 'number' && Number.isFinite(minSalary)
            ? Math.round(minSalary)
            : null
    const maxValue =
        typeof maxSalary === 'number' && Number.isFinite(maxSalary)
            ? Math.round(maxSalary)
            : null

    if (minValue !== null && maxValue !== null) {
        return `${currency}: ${minValue.toLocaleString()} - ${maxValue.toLocaleString()}`
    }
    if (minValue !== null) {
        return `${currency}: ${minValue.toLocaleString()}+`
    }
    if (maxValue !== null) {
        return `${currency}: up to ${maxValue.toLocaleString()}`
    }

    return `${currency}: -`
}

const formatTargetSalaryMention = (salary: {
    minSalaryGBP: number | null
    maxSalaryGBP: number | null
    minSalaryUSD: number | null
    maxSalaryUSD: number | null
    minSalaryEUR: number | null
    maxSalaryEUR: number | null
    inputCurrency: TargetSalaryCurrency
}): string => {
    return `Input ${salary.inputCurrency} | ${[
        formatSalaryLabel('GBP', salary.minSalaryGBP, salary.maxSalaryGBP),
        formatSalaryLabel('USD', salary.minSalaryUSD, salary.maxSalaryUSD),
        formatSalaryLabel('EUR', salary.minSalaryEUR, salary.maxSalaryEUR),
    ].join(' | ')}`
}

type FeedbackJobCandidate = {
    coreSignalJobId: string
    title: string | null
    companyName: string | null
    location: string | null
    currentStatus: KitConvoJobOfferedDto['status'] | null
    currentReason: string | null
}

const FEEDBACK_STATUSES: GiveJobsFeedbackStatus[] = [
    'Interested',
    'Not Interested',
]

const DEFAULT_REASON_BY_STATUS: Record<GiveJobsFeedbackStatus, string> = {
    Interested: 'Looks like a strong fit for what I want next.',
    'Not Interested':
        'This role does not match what I am looking for right now.',
}

const getDefaultFeedbackStatus = (
    status: KitConvoJobOfferedDto['status'] | null,
): GiveJobsFeedbackStatus => {
    if (status === 'Not Interested') {
        return 'Not Interested'
    }

    return 'Interested'
}

export default function KitToolTesterCard({
    activeConvoId,
    isActiveCall,
    apiKey,
    onApiKeyChange,
    jobsOffered,
}: Props): React.JSX.Element {
    const [companyNamesInput, setCompanyNamesInput] = useState('')
    const [targetJobTitlesInput, setTargetJobTitlesInput] = useState('')
    const [locationPairsInput, setLocationPairsInput] = useState('')
    const [targetSalaryMinInput, setTargetSalaryMinInput] = useState('')
    const [targetSalaryMaxInput, setTargetSalaryMaxInput] = useState('')
    const [targetSalaryCurrency, setTargetSalaryCurrency] =
        useState<TargetSalaryCurrency>('GBP')

    const [isCallingCompanies, setIsCallingCompanies] = useState(false)
    const [isCallingTargetJobTitles, setIsCallingTargetJobTitles] =
        useState(false)
    const [isCallingLocations, setIsCallingLocations] = useState(false)
    const [isCallingTargetSalary, setIsCallingTargetSalary] = useState(false)
    const [isCallingExecuteJobSearch, setIsCallingExecuteJobSearch] =
        useState(false)
    const [isCallingGiveJobsFeedback, setIsCallingGiveJobsFeedback] =
        useState(false)

    const [companyToolError, setCompanyToolError] = useState<string | null>(
        null,
    )
    const [targetJobTitleToolError, setTargetJobTitleToolError] = useState<
        string | null
    >(null)
    const [locationToolError, setLocationToolError] = useState<string | null>(
        null,
    )
    const [targetSalaryToolError, setTargetSalaryToolError] = useState<
        string | null
    >(null)
    const [executeJobSearchToolError, setExecuteJobSearchToolError] = useState<
        string | null
    >(null)
    const [giveJobsFeedbackToolError, setGiveJobsFeedbackToolError] = useState<
        string | null
    >(null)

    const [companyToolResult, setCompanyToolResult] =
        useState<UsedToWorkAtCompaniesMentionedRes | null>(null)
    const [targetJobTitleToolResult, setTargetJobTitleToolResult] =
        useState<TargetJobTitlesMentionedRes | null>(null)
    const [locationToolResult, setLocationToolResult] =
        useState<TargetJobLocationsMentionedRes | null>(null)
    const [targetSalaryToolResult, setTargetSalaryToolResult] =
        useState<TargetSalaryMentionedRes | null>(null)
    const [executeJobSearchToolResult, setExecuteJobSearchToolResult] =
        useState<ExecuteJobSearchRes | null>(null)
    const [giveJobsFeedbackToolResult, setGiveJobsFeedbackToolResult] =
        useState<GiveJobsFeedbackRes | null>(null)

    const [feedbackSelectionByJobId, setFeedbackSelectionByJobId] = useState<
        Record<string, boolean>
    >({})
    const [feedbackStatusByJobId, setFeedbackStatusByJobId] = useState<
        Record<string, GiveJobsFeedbackStatus>
    >({})
    const [feedbackReasonByJobId, setFeedbackReasonByJobId] = useState<
        Record<string, string>
    >({})

    const parsedCompanyNames = useMemo(
        () => parseCompanyNames(companyNamesInput),
        [companyNamesInput],
    )
    const parsedTargetJobTitles = useMemo(
        () => parseTargetJobTitles(targetJobTitlesInput),
        [targetJobTitlesInput],
    )
    const parsedTargetLocations = useMemo(
        () => parseTargetLocations(locationPairsInput),
        [locationPairsInput],
    )

    const availableJobsForFeedback = useMemo((): FeedbackJobCandidate[] => {
        const jobsById = new Map<string, FeedbackJobCandidate>()

        for (const job of executeJobSearchToolResult?.jobs ?? []) {
            const coreSignalJobId = job.coreSignalJobId?.trim()
            if (!coreSignalJobId) {
                continue
            }

            jobsById.set(coreSignalJobId, {
                coreSignalJobId,
                title: job.title ?? null,
                companyName: job.companyName ?? null,
                location: job.location ?? null,
                currentStatus: null,
                currentReason: null,
            })
        }

        for (const offeredJob of jobsOffered ?? []) {
            const coreSignalJobId = offeredJob.coreSignalJobId?.trim()
            if (!coreSignalJobId) {
                continue
            }

            const existing = jobsById.get(coreSignalJobId)
            jobsById.set(coreSignalJobId, {
                coreSignalJobId,
                title: offeredJob.title ?? existing?.title ?? null,
                companyName:
                    offeredJob.companyName ?? existing?.companyName ?? null,
                location: offeredJob.location ?? existing?.location ?? null,
                currentStatus:
                    offeredJob.status ?? existing?.currentStatus ?? null,
                currentReason:
                    offeredJob.reason ?? existing?.currentReason ?? null,
            })
        }

        return Array.from(jobsById.values())
    }, [executeJobSearchToolResult, jobsOffered])

    const feedbackJobIdsKey = useMemo(
        () =>
            availableJobsForFeedback
                .map((job) => job.coreSignalJobId)
                .join('|'),
        [availableJobsForFeedback],
    )

    useEffect(() => {
        setCompanyToolError(null)
        setTargetJobTitleToolError(null)
        setLocationToolError(null)
        setTargetSalaryToolError(null)
        setExecuteJobSearchToolError(null)
        setGiveJobsFeedbackToolError(null)
        setCompanyToolResult(null)
        setTargetJobTitleToolResult(null)
        setLocationToolResult(null)
        setTargetSalaryToolResult(null)
        setExecuteJobSearchToolResult(null)
        setGiveJobsFeedbackToolResult(null)
        setFeedbackSelectionByJobId({})
        setFeedbackStatusByJobId({})
        setFeedbackReasonByJobId({})
    }, [activeConvoId])

    useEffect(() => {
        setFeedbackSelectionByJobId((prev) => {
            const next: Record<string, boolean> = {}
            for (const job of availableJobsForFeedback) {
                next[job.coreSignalJobId] = prev[job.coreSignalJobId] ?? true
            }
            return next
        })

        setFeedbackStatusByJobId((prev) => {
            const next: Record<string, GiveJobsFeedbackStatus> = {}
            for (const job of availableJobsForFeedback) {
                next[job.coreSignalJobId] =
                    prev[job.coreSignalJobId] ??
                    getDefaultFeedbackStatus(job.currentStatus)
            }
            return next
        })

        setFeedbackReasonByJobId((prev) => {
            const next: Record<string, string> = {}
            for (const job of availableJobsForFeedback) {
                const defaultStatus = getDefaultFeedbackStatus(
                    job.currentStatus,
                )
                next[job.coreSignalJobId] =
                    prev[job.coreSignalJobId] ??
                    job.currentReason?.trim() ??
                    DEFAULT_REASON_BY_STATUS[defaultStatus]
            }
            return next
        })
    }, [feedbackJobIdsKey, availableJobsForFeedback])

    const handleRunCompanyToolCall = async () => {
        if (!activeConvoId) {
            setCompanyToolError('Start a Kit call before testing tool calls.')
            return
        }

        if (!isActiveCall) {
            setCompanyToolError(
                'Tool calls are only available while a call is active.',
            )
            return
        }

        const trimmedApiKey = apiKey.trim()
        if (!trimmedApiKey) {
            setCompanyToolError('Enter an API key to call this tool.')
            return
        }

        if (parsedCompanyNames.length === 0) {
            setCompanyToolError('Enter at least one company name to continue.')
            return
        }

        setIsCallingCompanies(true)
        setCompanyToolError(null)
        setCompanyToolResult(null)

        try {
            const result = await kitToolApi.usedToWorkAtCompaniesMentioned(
                { companyNames: parsedCompanyNames },
                { apiKey: trimmedApiKey, convId: activeConvoId },
            )
            setCompanyToolResult(result)
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to run the tool call.'
            setCompanyToolError(message)
        } finally {
            setIsCallingCompanies(false)
        }
    }

    const handleRunLocationToolCall = async () => {
        if (!activeConvoId) {
            setLocationToolError('Start a Kit call before testing tool calls.')
            return
        }

        if (!isActiveCall) {
            setLocationToolError(
                'Tool calls are only available while a call is active.',
            )
            return
        }

        const trimmedApiKey = apiKey.trim()
        if (!trimmedApiKey) {
            setLocationToolError('Enter an API key to call this tool.')
            return
        }

        if (parsedTargetLocations.length === 0) {
            setLocationToolError(
                'Enter at least one location using "City, Country" per line. City or country may be blank, but not both.',
            )
            return
        }

        setIsCallingLocations(true)
        setLocationToolError(null)
        setLocationToolResult(null)

        try {
            const result = await kitToolApi.targetJobLocationsMentioned(
                { locations: parsedTargetLocations },
                { apiKey: trimmedApiKey, convId: activeConvoId },
            )
            setLocationToolResult(result)
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to run the tool call.'
            setLocationToolError(message)
        } finally {
            setIsCallingLocations(false)
        }
    }

    const handleRunTargetJobTitleToolCall = async () => {
        if (!activeConvoId) {
            setTargetJobTitleToolError(
                'Start a Kit call before testing tool calls.',
            )
            return
        }

        if (!isActiveCall) {
            setTargetJobTitleToolError(
                'Tool calls are only available while a call is active.',
            )
            return
        }

        const trimmedApiKey = apiKey.trim()
        if (!trimmedApiKey) {
            setTargetJobTitleToolError('Enter an API key to call this tool.')
            return
        }

        if (parsedTargetJobTitles.length === 0) {
            setTargetJobTitleToolError(
                'Enter at least one target job title to continue.',
            )
            return
        }

        setIsCallingTargetJobTitles(true)
        setTargetJobTitleToolError(null)
        setTargetJobTitleToolResult(null)

        try {
            const result = await kitToolApi.targetJobTitleMentioned(
                { jobTitles: parsedTargetJobTitles },
                { apiKey: trimmedApiKey, convId: activeConvoId },
            )
            setTargetJobTitleToolResult(result)
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to run the tool call.'
            setTargetJobTitleToolError(message)
        } finally {
            setIsCallingTargetJobTitles(false)
        }
    }

    const handleRunTargetSalaryToolCall = async () => {
        if (!activeConvoId) {
            setTargetSalaryToolError(
                'Start a Kit call before testing tool calls.',
            )
            return
        }

        if (!isActiveCall) {
            setTargetSalaryToolError(
                'Tool calls are only available while a call is active.',
            )
            return
        }

        const trimmedApiKey = apiKey.trim()
        if (!trimmedApiKey) {
            setTargetSalaryToolError('Enter an API key to call this tool.')
            return
        }

        const parsedMinSalary =
            parseOptionalNonNegativeNumber(targetSalaryMinInput)
        if (parsedMinSalary === 'invalid') {
            setTargetSalaryToolError(
                'Minimum salary must be a non-negative number when provided.',
            )
            return
        }

        const parsedMaxSalary =
            parseOptionalNonNegativeNumber(targetSalaryMaxInput)
        if (parsedMaxSalary === 'invalid') {
            setTargetSalaryToolError(
                'Maximum salary must be a non-negative number when provided.',
            )
            return
        }

        if (parsedMinSalary === undefined && parsedMaxSalary === undefined) {
            setTargetSalaryToolError('Enter a min salary, max salary, or both.')
            return
        }

        if (
            parsedMinSalary !== undefined &&
            parsedMaxSalary !== undefined &&
            parsedMinSalary > parsedMaxSalary
        ) {
            setTargetSalaryToolError(
                'Minimum salary cannot be greater than maximum salary.',
            )
            return
        }

        setIsCallingTargetSalary(true)
        setTargetSalaryToolError(null)
        setTargetSalaryToolResult(null)

        try {
            const result = await kitToolApi.targetSalaryMentioned(
                {
                    minSalary: parsedMinSalary,
                    maxSalary: parsedMaxSalary,
                    currency: targetSalaryCurrency,
                },
                { apiKey: trimmedApiKey, convId: activeConvoId },
            )
            setTargetSalaryToolResult(result)
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to run the tool call.'
            setTargetSalaryToolError(message)
        } finally {
            setIsCallingTargetSalary(false)
        }
    }

    const handleRunExecuteJobSearchToolCall = async () => {
        if (!activeConvoId) {
            setExecuteJobSearchToolError(
                'Start a Kit call before testing tool calls.',
            )
            return
        }

        if (!isActiveCall) {
            setExecuteJobSearchToolError(
                'Tool calls are only available while a call is active.',
            )
            return
        }

        const trimmedApiKey = apiKey.trim()
        if (!trimmedApiKey) {
            setExecuteJobSearchToolError('Enter an API key to call this tool.')
            return
        }

        setIsCallingExecuteJobSearch(true)
        setExecuteJobSearchToolError(null)
        setExecuteJobSearchToolResult(null)

        try {
            const result = await kitToolApi.executeJobSearch(
                {},
                { apiKey: trimmedApiKey, convId: activeConvoId },
            )
            setExecuteJobSearchToolResult(result)
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to run the tool call.'
            setExecuteJobSearchToolError(message)
        } finally {
            setIsCallingExecuteJobSearch(false)
        }
    }

    const handleSetSelectedFeedbackStatus = (
        status: GiveJobsFeedbackStatus,
    ): void => {
        setFeedbackStatusByJobId((prev) => {
            const next = { ...prev }
            for (const job of availableJobsForFeedback) {
                if (!feedbackSelectionByJobId[job.coreSignalJobId]) {
                    continue
                }
                next[job.coreSignalJobId] = status
            }
            return next
        })

        setFeedbackReasonByJobId((prev) => {
            const next = { ...prev }
            for (const job of availableJobsForFeedback) {
                if (!feedbackSelectionByJobId[job.coreSignalJobId]) {
                    continue
                }
                const existingReason = next[job.coreSignalJobId]?.trim()
                if (!existingReason) {
                    next[job.coreSignalJobId] = DEFAULT_REASON_BY_STATUS[status]
                }
            }
            return next
        })
    }

    const handleSelectAllFeedbackJobs = (): void => {
        setFeedbackSelectionByJobId(() => {
            const next: Record<string, boolean> = {}
            for (const job of availableJobsForFeedback) {
                next[job.coreSignalJobId] = true
            }
            return next
        })
    }

    const handleClearAllFeedbackJobs = (): void => {
        setFeedbackSelectionByJobId(() => {
            const next: Record<string, boolean> = {}
            for (const job of availableJobsForFeedback) {
                next[job.coreSignalJobId] = false
            }
            return next
        })
    }

    const handleRunGiveJobsFeedbackToolCall = async () => {
        if (!activeConvoId) {
            setGiveJobsFeedbackToolError(
                'Start a Kit call before testing tool calls.',
            )
            return
        }

        if (!isActiveCall) {
            setGiveJobsFeedbackToolError(
                'Tool calls are only available while a call is active.',
            )
            return
        }

        const trimmedApiKey = apiKey.trim()
        if (!trimmedApiKey) {
            setGiveJobsFeedbackToolError('Enter an API key to call this tool.')
            return
        }

        const selectedJobs = availableJobsForFeedback.filter(
            (job) => feedbackSelectionByJobId[job.coreSignalJobId],
        )
        if (selectedJobs.length === 0) {
            setGiveJobsFeedbackToolError(
                'Select at least one job to submit feedback.',
            )
            return
        }

        const feedbackPayload = selectedJobs.map((job) => {
            const status =
                feedbackStatusByJobId[job.coreSignalJobId] ??
                getDefaultFeedbackStatus(job.currentStatus)
            const reason =
                feedbackReasonByJobId[job.coreSignalJobId]?.trim() ?? ''
            return {
                coreSignalJobId: job.coreSignalJobId,
                status,
                reason,
            }
        })
        const entriesWithMissingReason = feedbackPayload.filter(
            (entry) => entry.reason.length === 0,
        )

        if (entriesWithMissingReason.length > 0) {
            setGiveJobsFeedbackToolError(
                'Each selected job needs a reason before submission.',
            )
            return
        }

        setIsCallingGiveJobsFeedback(true)
        setGiveJobsFeedbackToolError(null)
        setGiveJobsFeedbackToolResult(null)

        try {
            const result = await kitToolApi.giveJobsFeedback(
                {
                    jobFeedbacks: feedbackPayload,
                },
                {
                    apiKey: trimmedApiKey,
                    convId: activeConvoId,
                },
            )
            setGiveJobsFeedbackToolResult(result)
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to run the tool call.'
            setGiveJobsFeedbackToolError(message)
        } finally {
            setIsCallingGiveJobsFeedback(false)
        }
    }

    const canSubmitCompanies =
        Boolean(activeConvoId) &&
        isActiveCall &&
        parsedCompanyNames.length > 0 &&
        apiKey.trim().length > 0 &&
        !isCallingCompanies

    const canSubmitLocations =
        Boolean(activeConvoId) &&
        isActiveCall &&
        parsedTargetLocations.length > 0 &&
        apiKey.trim().length > 0 &&
        !isCallingLocations

    const canSubmitTargetJobTitles =
        Boolean(activeConvoId) &&
        isActiveCall &&
        parsedTargetJobTitles.length > 0 &&
        apiKey.trim().length > 0 &&
        !isCallingTargetJobTitles

    const hasTargetSalaryInput =
        targetSalaryMinInput.trim().length > 0 ||
        targetSalaryMaxInput.trim().length > 0

    const canSubmitTargetSalary =
        Boolean(activeConvoId) &&
        isActiveCall &&
        apiKey.trim().length > 0 &&
        hasTargetSalaryInput &&
        !isCallingTargetSalary

    const canSubmitExecuteJobSearch =
        Boolean(activeConvoId) &&
        isActiveCall &&
        apiKey.trim().length > 0 &&
        !isCallingExecuteJobSearch

    const selectedFeedbackCount = availableJobsForFeedback.filter(
        (job) => feedbackSelectionByJobId[job.coreSignalJobId],
    ).length

    const canSubmitGiveJobsFeedback =
        Boolean(activeConvoId) &&
        isActiveCall &&
        apiKey.trim().length > 0 &&
        !isCallingGiveJobsFeedback &&
        selectedFeedbackCount > 0

    const companyEntryCountLabel = companyToolResult
        ? `${companyToolResult.companyResolutionData.length} ${
              companyToolResult.companyResolutionData.length === 1
                  ? 'entry'
                  : 'entries'
          }`
        : ''

    const locationEntryCountLabel = locationToolResult
        ? `${locationToolResult.locationMentions.length} ${
              locationToolResult.locationMentions.length === 1
                  ? 'entry'
                  : 'entries'
          }`
        : ''

    const targetJobTitleEntryCountLabel = targetJobTitleToolResult
        ? `${targetJobTitleToolResult.targetJobTitleMentions.length} ${
              targetJobTitleToolResult.targetJobTitleMentions.length === 1
                  ? 'entry'
                  : 'entries'
          }`
        : ''

    const targetSalaryEntryCountLabel = targetSalaryToolResult ? '1 entry' : ''

    return (
        <div className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)] space-y-4">
            <div>
                <h2 className="text-lg font-semibold text-foreground">
                    Tool Call Tester
                </h2>
                <p className="text-sm text-muted-foreground">
                    Trigger Kit tool calls against the active conversation.
                </p>
            </div>

            {!activeConvoId ? (
                <Note type={NoteType.INFO}>
                    Start a call to unlock tool testing.
                </Note>
            ) : null}
            {activeConvoId && !isActiveCall ? (
                <Note type={NoteType.INFO}>
                    Tool calls are available while a call is active.
                </Note>
            ) : null}
            {activeConvoId ? (
                <p className="text-xs text-muted-foreground break-all">
                    convId: {activeConvoId}
                </p>
            ) : null}

            <div className="space-y-2">
                <label
                    htmlFor="kit-test-api-key"
                    className="text-sm font-semibold text-foreground"
                >
                    API key
                </label>
                <input
                    id="kit-test-api-key"
                    className="form-input"
                    placeholder="Enter API key"
                    value={apiKey}
                    onChange={(event) => onApiKeyChange(event.target.value)}
                    disabled={isActiveCall}
                />
                <p className="text-xs text-muted-foreground">
                    This key is locked while a call is active.
                </p>
            </div>

            <div className="space-y-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground uppercase tracking-wide">
                    <span>Tools</span>
                    <span>6 available</span>
                </div>

                <div className="rounded-2xl border border-border bg-white/70 p-4 shadow-[var(--shadow-soft)] space-y-4">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <p className="text-sm font-semibold text-foreground">
                                used-to-work-at-companies-mentioned
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Provide company names to resolve and log against
                                Kit.
                            </p>
                        </div>
                        <span className="rounded-full bg-secondary px-2 py-1 text-[10px] font-semibold text-muted-foreground">
                            Tool
                        </span>
                    </div>

                    <div>
                        <label
                            htmlFor="kit-test-companies"
                            className="text-sm font-semibold text-foreground"
                        >
                            Company names
                        </label>
                        <textarea
                            id="kit-test-companies"
                            className="form-input mt-1 min-h-[120px]"
                            placeholder="Monzo&#10;Revolut&#10;Deliveroo"
                            value={companyNamesInput}
                            onChange={(event) =>
                                setCompanyNamesInput(event.target.value)
                            }
                            disabled={isCallingCompanies}
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                            {parsedCompanyNames.length > 0
                                ? `${parsedCompanyNames.length} company name${
                                      parsedCompanyNames.length === 1 ? '' : 's'
                                  } detected.`
                                : 'Separate names with commas or new lines.'}
                        </p>
                    </div>
                    <Button
                        type="button"
                        style="primary"
                        enabled={canSubmitCompanies}
                        classes="w-full justify-center"
                        onClick={handleRunCompanyToolCall}
                    >
                        {isCallingCompanies
                            ? 'Running tool call...'
                            : 'Run tool call'}
                    </Button>

                    {companyToolError ? (
                        <Note type={NoteType.ERROR}>{companyToolError}</Note>
                    ) : null}

                    {companyToolResult ? (
                        <div className="rounded-2xl border border-border bg-secondary/40 p-4 space-y-3">
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>Response</span>
                                <span>{companyEntryCountLabel}</span>
                            </div>
                            <div className="space-y-3">
                                {companyToolResult.companyResolutionData.map(
                                    (entry, index) => (
                                        <div
                                            key={`${entry.inputCompanyName}-${index}`}
                                            className="rounded-xl border border-border/70 bg-white/70 p-3"
                                        >
                                            <p className="text-xs font-semibold text-foreground">
                                                {entry.inputCompanyName}
                                            </p>
                                            {entry.resolvedCompanies.length >
                                            0 ? (
                                                <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                                                    {entry.resolvedCompanies.map(
                                                        (company) => (
                                                            <li
                                                                key={
                                                                    company.companyId
                                                                }
                                                                className="flex items-center justify-between gap-2"
                                                            >
                                                                <span>
                                                                    {
                                                                        company.companyName
                                                                    }
                                                                </span>
                                                                <span className="text-[10px] text-muted-foreground/70">
                                                                    {
                                                                        company.companyId
                                                                    }
                                                                </span>
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            ) : (
                                                <p className="mt-2 text-xs text-muted-foreground">
                                                    No matches returned.
                                                </p>
                                            )}
                                        </div>
                                    ),
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="rounded-2xl border border-border bg-white/70 p-4 shadow-[var(--shadow-soft)] space-y-4">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <p className="text-sm font-semibold text-foreground">
                                target-salary-mentioned
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Log target salary mentions and link them to
                                current search params.
                            </p>
                        </div>
                        <span className="rounded-full bg-secondary px-2 py-1 text-[10px] font-semibold text-muted-foreground">
                            Tool
                        </span>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <label
                                htmlFor="kit-test-target-salary-min"
                                className="text-sm font-semibold text-foreground"
                            >
                                Min salary
                            </label>
                            <input
                                id="kit-test-target-salary-min"
                                className="form-input mt-1"
                                placeholder="50000"
                                inputMode="decimal"
                                value={targetSalaryMinInput}
                                onChange={(event) =>
                                    setTargetSalaryMinInput(event.target.value)
                                }
                                disabled={isCallingTargetSalary}
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="kit-test-target-salary-max"
                                className="text-sm font-semibold text-foreground"
                            >
                                Max salary
                            </label>
                            <input
                                id="kit-test-target-salary-max"
                                className="form-input mt-1"
                                placeholder="90000"
                                inputMode="decimal"
                                value={targetSalaryMaxInput}
                                onChange={(event) =>
                                    setTargetSalaryMaxInput(event.target.value)
                                }
                                disabled={isCallingTargetSalary}
                            />
                        </div>
                    </div>

                    <div>
                        <label
                            htmlFor="kit-test-target-salary-currency"
                            className="text-sm font-semibold text-foreground"
                        >
                            Currency
                        </label>
                        <select
                            id="kit-test-target-salary-currency"
                            className="form-input mt-1"
                            value={targetSalaryCurrency}
                            onChange={(event) =>
                                setTargetSalaryCurrency(
                                    event.target.value as TargetSalaryCurrency,
                                )
                            }
                            disabled={isCallingTargetSalary}
                        >
                            {TARGET_SALARY_CURRENCIES.map((currency) => (
                                <option key={currency} value={currency}>
                                    {currency}
                                </option>
                            ))}
                        </select>
                        <p className="mt-1 text-xs text-muted-foreground">
                            Provide min, max, or both. Currency must be GBP,
                            USD, or EUR.
                        </p>
                    </div>

                    <Button
                        type="button"
                        style="primary"
                        enabled={canSubmitTargetSalary}
                        classes="w-full justify-center"
                        onClick={handleRunTargetSalaryToolCall}
                    >
                        {isCallingTargetSalary
                            ? 'Running tool call...'
                            : 'Run tool call'}
                    </Button>

                    {targetSalaryToolError ? (
                        <Note type={NoteType.ERROR}>
                            {targetSalaryToolError}
                        </Note>
                    ) : null}

                    {targetSalaryToolResult ? (
                        <div className="rounded-2xl border border-border bg-secondary/40 p-4 space-y-3">
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>Response</span>
                                <span>{targetSalaryEntryCountLabel}</span>
                            </div>
                            <div className="rounded-xl border border-border/70 bg-white/70 p-3">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-xs font-semibold text-foreground">
                                        {formatTargetSalaryMention(
                                            targetSalaryToolResult.targetSalaryMention,
                                        )}
                                    </p>
                                    <span className="text-[10px] text-muted-foreground/70">
                                        {
                                            targetSalaryToolResult
                                                .targetSalaryMention
                                                .targetSalaryMentionId
                                        }
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="rounded-2xl border border-border bg-white/70 p-4 shadow-[var(--shadow-soft)] space-y-4">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <p className="text-sm font-semibold text-foreground">
                                target-job-title-mentioned
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Log target job title mentions and link them to
                                current search params.
                            </p>
                        </div>
                        <span className="rounded-full bg-secondary px-2 py-1 text-[10px] font-semibold text-muted-foreground">
                            Tool
                        </span>
                    </div>

                    <div>
                        <label
                            htmlFor="kit-test-target-job-titles"
                            className="text-sm font-semibold text-foreground"
                        >
                            Job titles
                        </label>
                        <textarea
                            id="kit-test-target-job-titles"
                            className="form-input mt-1 min-h-[120px]"
                            placeholder="Software Engineer&#10;Engineering Manager&#10;Head of Product"
                            value={targetJobTitlesInput}
                            onChange={(event) =>
                                setTargetJobTitlesInput(event.target.value)
                            }
                            disabled={isCallingTargetJobTitles}
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                            {parsedTargetJobTitles.length > 0
                                ? `${parsedTargetJobTitles.length} title${
                                      parsedTargetJobTitles.length === 1
                                          ? ''
                                          : 's'
                                  } detected.`
                                : 'Separate titles with commas or new lines. Titles are trimmed and deduplicated.'}
                        </p>
                    </div>
                    <Button
                        type="button"
                        style="primary"
                        enabled={canSubmitTargetJobTitles}
                        classes="w-full justify-center"
                        onClick={handleRunTargetJobTitleToolCall}
                    >
                        {isCallingTargetJobTitles
                            ? 'Running tool call...'
                            : 'Run tool call'}
                    </Button>

                    {targetJobTitleToolError ? (
                        <Note type={NoteType.ERROR}>
                            {targetJobTitleToolError}
                        </Note>
                    ) : null}

                    {targetJobTitleToolResult ? (
                        <div className="rounded-2xl border border-border bg-secondary/40 p-4 space-y-3">
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>Response</span>
                                <span>{targetJobTitleEntryCountLabel}</span>
                            </div>
                            <div className="space-y-2">
                                {targetJobTitleToolResult.targetJobTitleMentions
                                    .length === 0 ? (
                                    <p className="text-xs text-muted-foreground">
                                        No target job title mentions returned.
                                    </p>
                                ) : (
                                    targetJobTitleToolResult.targetJobTitleMentions.map(
                                        (mention) => (
                                            <div
                                                key={
                                                    mention.targetJobTitleMentionId
                                                }
                                                className="rounded-xl border border-border/70 bg-white/70 p-3"
                                            >
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="text-xs font-semibold text-foreground">
                                                        {mention.jobTitle}
                                                    </p>
                                                    <span className="text-[10px] text-muted-foreground/70">
                                                        {
                                                            mention.targetJobTitleMentionId
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        ),
                                    )
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="rounded-2xl border border-border bg-white/70 p-4 shadow-[var(--shadow-soft)] space-y-4">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <p className="text-sm font-semibold text-foreground">
                                target-job-locations-mentioned
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Log target location mentions and link them to
                                current search params.
                            </p>
                        </div>
                        <span className="rounded-full bg-secondary px-2 py-1 text-[10px] font-semibold text-muted-foreground">
                            Tool
                        </span>
                    </div>

                    <div>
                        <label
                            htmlFor="kit-test-target-locations"
                            className="text-sm font-semibold text-foreground"
                        >
                            City / country pairs
                        </label>
                        <textarea
                            id="kit-test-target-locations"
                            className="form-input mt-1 min-h-[120px]"
                            placeholder="Manchester,United Kingdom&#10;Manchester, United Kingdom&#10;, United Kingdom&#10;Manchester,"
                            value={locationPairsInput}
                            onChange={(event) =>
                                setLocationPairsInput(event.target.value)
                            }
                            disabled={isCallingLocations}
                        />
                        <p className="mt-1 text-xs text-muted-foreground">
                            {parsedTargetLocations.length > 0
                                ? `${parsedTargetLocations.length} location pair${
                                      parsedTargetLocations.length === 1
                                          ? ''
                                          : 's'
                                  } detected.`
                                : 'Use one line per location in "City, Country" format. City/country are trimmed and either side may be empty.'}
                        </p>
                    </div>
                    <Button
                        type="button"
                        style="primary"
                        enabled={canSubmitLocations}
                        classes="w-full justify-center"
                        onClick={handleRunLocationToolCall}
                    >
                        {isCallingLocations
                            ? 'Running tool call...'
                            : 'Run tool call'}
                    </Button>

                    {locationToolError ? (
                        <Note type={NoteType.ERROR}>{locationToolError}</Note>
                    ) : null}

                    {locationToolResult ? (
                        <div className="rounded-2xl border border-border bg-secondary/40 p-4 space-y-3">
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>Response</span>
                                <span>{locationEntryCountLabel}</span>
                            </div>
                            <div className="space-y-2">
                                {locationToolResult.locationMentions.length ===
                                0 ? (
                                    <p className="text-xs text-muted-foreground">
                                        No location mentions returned.
                                    </p>
                                ) : (
                                    locationToolResult.locationMentions.map(
                                        (mention) => (
                                            <div
                                                key={mention.locationMentionId}
                                                className="rounded-xl border border-border/70 bg-white/70 p-3"
                                            >
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="text-xs font-semibold text-foreground">
                                                        {formatLocationMention(
                                                            mention.city,
                                                            mention.country,
                                                        )}
                                                    </p>
                                                    <span className="text-[10px] text-muted-foreground/70">
                                                        {
                                                            mention.locationMentionId
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                        ),
                                    )
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="rounded-2xl border border-border bg-white/70 p-4 shadow-[var(--shadow-soft)] space-y-4">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <p className="text-sm font-semibold text-foreground">
                                execute-job-search
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Execute the full search workflow and return the
                                top jobs for the conversation context.
                            </p>
                        </div>
                        <span className="rounded-full bg-secondary px-2 py-1 text-[10px] font-semibold text-muted-foreground">
                            Tool
                        </span>
                    </div>

                    <Button
                        type="button"
                        style="primary"
                        enabled={canSubmitExecuteJobSearch}
                        classes="w-full justify-center"
                        onClick={handleRunExecuteJobSearchToolCall}
                    >
                        {isCallingExecuteJobSearch
                            ? 'Running tool call...'
                            : 'Run tool call'}
                    </Button>

                    {executeJobSearchToolError ? (
                        <Note type={NoteType.ERROR}>
                            {executeJobSearchToolError}
                        </Note>
                    ) : null}

                    {executeJobSearchToolResult ? (
                        <div className="rounded-2xl border border-border bg-secondary/40 p-4 space-y-3">
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>Response</span>
                                <span>
                                    {executeJobSearchToolResult.jobs.length}{' '}
                                    returned /{' '}
                                    {
                                        executeJobSearchToolResult.totalPreviewResults
                                    }{' '}
                                    preview results
                                </span>
                            </div>

                            <div className="rounded-xl border border-border/70 bg-white/70 p-3 text-xs text-muted-foreground">
                                <p className="break-all">
                                    executionId:{' '}
                                    <span className="text-foreground">
                                        {
                                            executeJobSearchToolResult.jobSearchExecutionId
                                        }
                                    </span>
                                </p>
                                <p className="mt-1">
                                    generated titles:{' '}
                                    <span className="text-foreground">
                                        {
                                            executeJobSearchToolResult
                                                .generatedJobTitles.length
                                        }
                                    </span>
                                </p>
                            </div>

                            {executeJobSearchToolResult.jobs.length === 0 ? (
                                <p className="text-xs text-muted-foreground">
                                    No jobs returned.
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {executeJobSearchToolResult.jobs.map(
                                        (job) => (
                                            <div
                                                key={job.coreSignalJobId}
                                                className="rounded-xl border border-border/70 bg-white/70 p-3"
                                            >
                                                <p className="text-xs font-semibold text-foreground">
                                                    {job.title ??
                                                        'Title unavailable'}
                                                </p>
                                                <p className="mt-1 text-[11px] text-muted-foreground">
                                                    {job.companyName ??
                                                        'Company unavailable'}{' '}
                                                    •{' '}
                                                    {job.location ??
                                                        'Location unavailable'}
                                                </p>
                                                <p className="mt-1 text-[11px] text-muted-foreground break-all">
                                                    coreSignalId:{' '}
                                                    {job.coreSignalId}
                                                </p>
                                                <p className="text-[11px] text-muted-foreground">
                                                    rankScore:{' '}
                                                    {job.rankScore ?? '-'}
                                                </p>
                                            </div>
                                        ),
                                    )}
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>

                <div className="rounded-2xl border border-border bg-white/70 p-4 shadow-[var(--shadow-soft)] space-y-4">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <p className="text-sm font-semibold text-foreground">
                                give-jobs-feedback
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Update `JOB_OFFERED` status and reason for jobs
                                already offered in this conversation.
                            </p>
                        </div>
                        <span className="rounded-full bg-secondary px-2 py-1 text-[10px] font-semibold text-muted-foreground">
                            Tool
                        </span>
                    </div>

                    <p className="text-xs text-muted-foreground">
                        Job IDs are auto-loaded from this conversation. Run
                        `execute-job-search` or use existing offered jobs.
                    </p>

                    {availableJobsForFeedback.length === 0 ? (
                        <Note type={NoteType.INFO}>
                            No jobs available yet. Run `execute-job-search` to
                            fetch jobs, then submit feedback here.
                        </Note>
                    ) : (
                        <div className="space-y-3">
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                <button
                                    type="button"
                                    className="rounded-full border border-border bg-white px-3 py-1 text-foreground hover:bg-secondary"
                                    onClick={handleSelectAllFeedbackJobs}
                                >
                                    Select all
                                </button>
                                <button
                                    type="button"
                                    className="rounded-full border border-border bg-white px-3 py-1 text-foreground hover:bg-secondary"
                                    onClick={handleClearAllFeedbackJobs}
                                >
                                    Clear all
                                </button>
                                <button
                                    type="button"
                                    className="rounded-full border border-border bg-white px-3 py-1 text-foreground hover:bg-secondary"
                                    onClick={() =>
                                        handleSetSelectedFeedbackStatus(
                                            'Interested',
                                        )
                                    }
                                >
                                    Selected: Interested
                                </button>
                                <button
                                    type="button"
                                    className="rounded-full border border-border bg-white px-3 py-1 text-foreground hover:bg-secondary"
                                    onClick={() =>
                                        handleSetSelectedFeedbackStatus(
                                            'Not Interested',
                                        )
                                    }
                                >
                                    Selected: Not Interested
                                </button>
                                <span className="text-muted-foreground">
                                    {selectedFeedbackCount} selected
                                </span>
                            </div>

                            <div className="space-y-2">
                                {availableJobsForFeedback.map((job) => {
                                    const selected = Boolean(
                                        feedbackSelectionByJobId[
                                            job.coreSignalJobId
                                        ],
                                    )
                                    const status =
                                        feedbackStatusByJobId[
                                            job.coreSignalJobId
                                        ] ??
                                        getDefaultFeedbackStatus(
                                            job.currentStatus,
                                        )
                                    const reason =
                                        feedbackReasonByJobId[
                                            job.coreSignalJobId
                                        ] ?? ''

                                    return (
                                        <div
                                            key={job.coreSignalJobId}
                                            className="rounded-xl border border-border/70 bg-white/70 p-3 space-y-2"
                                        >
                                            <div className="flex flex-wrap items-start justify-between gap-2">
                                                <label className="flex items-start gap-2 text-xs text-foreground">
                                                    <input
                                                        type="checkbox"
                                                        className="mt-0.5"
                                                        checked={selected}
                                                        onChange={(event) =>
                                                            setFeedbackSelectionByJobId(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    [job.coreSignalJobId]:
                                                                        event
                                                                            .target
                                                                            .checked,
                                                                }),
                                                            )
                                                        }
                                                    />
                                                    <span>
                                                        <span className="font-semibold">
                                                            {job.title ??
                                                                'Title unavailable'}
                                                        </span>
                                                        <span className="text-muted-foreground">
                                                            {' '}
                                                            •{' '}
                                                            {job.companyName ??
                                                                'Company unavailable'}{' '}
                                                            •{' '}
                                                            {job.location ??
                                                                'Location unavailable'}
                                                        </span>
                                                    </span>
                                                </label>
                                                <span className="text-[10px] text-muted-foreground break-all">
                                                    {job.coreSignalJobId}
                                                </span>
                                            </div>

                                            <div className="text-[11px] text-muted-foreground">
                                                Current status:{' '}
                                                <span className="text-foreground">
                                                    {job.currentStatus ??
                                                        'Offered/unknown'}
                                                </span>
                                                {job.currentReason ? (
                                                    <span>
                                                        {' '}
                                                        | Current reason:{' '}
                                                        <span className="text-foreground">
                                                            {job.currentReason}
                                                        </span>
                                                    </span>
                                                ) : null}
                                            </div>

                                            <div className="grid gap-2 sm:grid-cols-[180px_minmax(0,1fr)]">
                                                <select
                                                    className="form-input"
                                                    value={status}
                                                    disabled={!selected}
                                                    onChange={(event) =>
                                                        setFeedbackStatusByJobId(
                                                            (prev) => ({
                                                                ...prev,
                                                                [job.coreSignalJobId]:
                                                                    event.target
                                                                        .value as GiveJobsFeedbackStatus,
                                                            }),
                                                        )
                                                    }
                                                >
                                                    {FEEDBACK_STATUSES.map(
                                                        (entry) => (
                                                            <option
                                                                key={entry}
                                                                value={entry}
                                                            >
                                                                {entry}
                                                            </option>
                                                        ),
                                                    )}
                                                </select>
                                                <input
                                                    className="form-input"
                                                    placeholder="Reason for this status"
                                                    value={reason}
                                                    disabled={!selected}
                                                    onChange={(event) =>
                                                        setFeedbackReasonByJobId(
                                                            (prev) => ({
                                                                ...prev,
                                                                [job.coreSignalJobId]:
                                                                    event.target
                                                                        .value,
                                                            }),
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                    <Button
                        type="button"
                        style="primary"
                        enabled={canSubmitGiveJobsFeedback}
                        classes="w-full justify-center"
                        onClick={handleRunGiveJobsFeedbackToolCall}
                    >
                        {isCallingGiveJobsFeedback
                            ? 'Running tool call...'
                            : 'Submit jobs feedback'}
                    </Button>

                    {giveJobsFeedbackToolError ? (
                        <Note type={NoteType.ERROR}>
                            {giveJobsFeedbackToolError}
                        </Note>
                    ) : null}

                    {giveJobsFeedbackToolResult ? (
                        <div className="rounded-2xl border border-border bg-secondary/40 p-4 space-y-2">
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>Response</span>
                                <span>
                                    {
                                        giveJobsFeedbackToolResult
                                            .updatedCoreSignalJobIds.length
                                    }{' '}
                                    updated
                                </span>
                            </div>
                            <div className="space-y-1">
                                {giveJobsFeedbackToolResult.updatedCoreSignalJobIds.map(
                                    (coreSignalJobId) => (
                                        <p
                                            key={coreSignalJobId}
                                            className="text-xs text-foreground break-all"
                                        >
                                            {coreSignalJobId}
                                        </p>
                                    ),
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    )
}
